import is from "@sindresorhus/is";
import {
	graphql,
	type PageProps as GatsbyPageProps,
	type HeadFC,
} from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { createContext, useEffect, type FC } from "react";
import { HeadMetaData } from "../../containers/headMetaData";
import PageContainerComponent from "../../containers/page";
import { SEO } from "../../containers/seo";
import { useStoryblokPreviewData } from "../../hooks/use-storyblok-preview";
import type { GenericPageProps } from "../../type-definitions/pages";
import type { Mutable } from "../../type-definitions/utility-types";
import { getBreadcrumbs } from "../../utils/breadcrumbs";
import { generateCanonicalUrl } from "../../utils/canonicalUrl";
import { pushCustomGTMEvent } from "../../utils/gtm/gtm-handlers";
import { findTranslatedSlugNameForSite } from "../../utils/storyblok/utils";
import { PageComponent } from "./page.component";

export type PageProps = GenericPageProps;

export const PageContext = createContext({ publishedAt: "" });

const Page: FC<GatsbyPageProps<Queries.contentPageDataQuery, PageProps>> = (
	props,
) => {
	const { t } = useTranslation();
	const page: Mutable<typeof props.data.storyblokEntry> =
		props.data.storyblokEntry;
	const pageContent = useStoryblokPreviewData({
		...page,
		_uid: page?._uid ?? "",
		component: page?.component ?? "",
	});

	const modules =
		pageContent?.modules && pageContent.modules.length > 0
			? pageContent.modules.map((module) => module as Mutable<typeof module>)
			: [];

	useEffect(() => {
		pushCustomGTMEvent({ type: "setPageType", data: { pageType: "homepage" } });
	}, []);

	if (!pageContent) return null;

	const homeLabel = t("Home");
	const isStartPage = props.data.startPagesWithThisId.count > 0;
	const name = findTranslatedSlugNameForSite(pageContent);

	const title = name;
	const header = pageContent.title ?? title;
	const breadcrumbLabel = is.nonEmptyString(pageContent.title)
		? pageContent.title
		: name;
	const backgroundColor = pageContent.background_color ?? undefined;
	const breadcrumbs = isStartPage
		? undefined
		: getBreadcrumbs(breadcrumbLabel, homeLabel, pageContent.breadcrumbs);
	return (
		<PageContainerComponent title={title} isStartPage={isStartPage}>
			<PageComponent
				title={header}
				backgroundColor={backgroundColor}
				modules={modules}
				breadcrumbs={breadcrumbs}
				published_at={props.pageContext.published_at}
			/>
		</PageContainerComponent>
	);
};

export const query = graphql`
	query contentPageData(
		$language: String!
		$storyblokLanguage: String!
		$storyblokUuid: String!
	) {
		# Used to load data for the i18next lib to use in its translations.
		...Locales

		siteConfig {
			name
		}

		storyblokEntry(
			field_component: { eq: "page" }
			lang: { eq: $storyblokLanguage }
			uuid: { eq: $storyblokUuid }
		) {
			_uid
			name
			full_slug
			uuid
			title: field_title_string
			background_color: field_background_color_string
			component: field_component
			full_slug
			translated_slugs {
				name
				lang
			}
			...Modules
			...SeoMetaData
			breadcrumbs {
				...Button
			}
		}
		startPagesWithThisId: allParsedSiteConfig(
			filter: { startpage: { eq: $storyblokUuid } }
		) {
			count: totalCount
		}
	}
`;

export default Page;

export const Head: HeadFC<Queries.contentPageDataQuery> = (props) => {
	const page: Mutable<typeof props.data.storyblokEntry> =
		props.data.storyblokEntry;
	const name = findTranslatedSlugNameForSite(page);
	const isStartPage = props.location.pathname === "/";

	const cannonicalUrl = generateCanonicalUrl(
		props.data.siteConfig?.name ?? "",
		props.location.pathname,
	);

	return (
		<>
			<HeadMetaData />
			<SEO
				localesData={props.data.locales}
				title={page?.meta_title || name || ""}
				description={page?.meta_description || ""}
				noIndex={Boolean(page?.no_index)}
				noFollow={Boolean(page?.no_follow)}
				isStartPage={isStartPage}
				canonicalUrl={cannonicalUrl}
			/>
		</>
	);
};
